/**
 * "Bulk Import Process by Rob Nunez" — the tag on a history entry that came
 * from a bulk run (docs/01 §7, ADR-67, punch 7.1; 0071).
 *
 * ONE COMPONENT, because two surfaces render it: a record's own History tab and
 * the admin ledger at /admin/audit. docs/01 §7 already states the rule for the
 * two things it sits beside — the action catalog and the field labels — and for
 * the same reason: "the admin ledger reads through the same two modules, so it
 * and the tabs cannot word one change two ways."
 *
 * The sentence itself comes from `bulkTag()` in `@propos/shared`, composed from
 * the batch's kind and THIS ROW'S actor. Nothing about the tag is stored, so it
 * can never name somebody other than the person the ledger records.
 *
 * A chip, not a sentence in the meta line, because it answers a different
 * question from the rest of the row. The row says what changed; this says the
 * change was one of four hundred and was not aimed at this record personally —
 * which is exactly what a property manager wants to know before asking a
 * colleague why they touched their building.
 */

import { bulkTag, isBulkBatchKind } from '@propos/shared';
import type { ReactNode } from 'react';

export function BulkTag({
  kind,
  actorName,
}: {
  readonly kind: string | null;
  readonly actorName: string | null;
}): ReactNode {
  // An unknown kind renders NOTHING rather than a guess. The server sends what
  // the database holds, and a build reading a row written by a newer one would
  // otherwise print a raw enum on a customer's screen — the exact failure the
  // audit catalog exists to prevent for actions.
  if (kind === null || !isBulkBatchKind(kind)) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-info-bg px-2 py-0.5 text-chip font-semibold text-info-fg">
      {bulkTag(kind, actorName)}
    </span>
  );
}
